import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Callout,
  DatePicker,
  EmptyState,
  Popover,
  SelectField,
  Spinner,
  Table,
  Tag,
  TextField,
  type SelectFieldOption,
  type PopoverHandle,
  type TableColumn,
} from '@humanforce/mango-ui-react';
import {
  getPayRunAuditEvents,
  PayRunAuditApiError,
  type PayRunAuditEvent,
  type PayRunAuditPage,
} from './payRunAuditApi';
import './PayRunAuditTrailTab.css';

interface PayRunAuditTrailTabProps {
  businessRecordId: string;
  payId: string;
}

interface DateRange {
  start: string;
  end: string;
}

const PAGE_SIZE = 50;

const utcDayBoundary = (value: string, followingDay = false) => {
  if (!value) return undefined;
  const date = new Date(`${value}T00:00:00.000Z`);
  if (followingDay) date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString();
};

const formatTime = (value: string) => {
  const date = new Date(value);
  return {
    time: new Intl.DateTimeFormat('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false }).format(date),
    date: new Intl.DateTimeFormat('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }).format(date),
  };
};

const titleCase = (value: string) => value
  .replaceAll(/[._-]+/g, ' ')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const outcomeTone = (outcome: string): 'positive' | 'caution' | 'critical' | 'neutral' => {
  const normalized = outcome.toLowerCase();
  if (normalized.includes('success') || normalized.includes('complete')) return 'positive';
  if (normalized.includes('block') || normalized.includes('fail') || normalized.includes('reject')) return 'critical';
  if (normalized.includes('warn')) return 'caution';
  return 'neutral';
};

export const PayRunAuditTrailTab = ({ businessRecordId, payId }: PayRunAuditTrailTabProps) => {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [dateRange, setDateRange] = useState<DateRange>({ start: '', end: '' });
  const [draftDateRange, setDraftDateRange] = useState<DateRange>({ start: '', end: '' });
  const [dateSelectionStep, setDateSelectionStep] = useState<'start' | 'end'>('start');
  const [hoveredDate, setHoveredDate] = useState<Date | null>(null);
  const [cursor, setCursor] = useState<string>();
  const [cursorHistory, setCursorHistory] = useState<Array<string | undefined>>([]);
  const [page, setPage] = useState<PayRunAuditPage>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [unavailable, setUnavailable] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const requestId = useRef(0);
  const dateRangePopoverRef = useRef<PopoverHandle>(null);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => window.clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    setCursor(undefined);
    setCursorHistory([]);
  }, [debouncedSearch, employeeId, dateRange.start, dateRange.end]);

  useEffect(() => {
    const abortController = new AbortController();
    const currentRequest = ++requestId.current;
    setLoading(true);
    setError(undefined);
    setUnavailable(false);

    void getPayRunAuditEvents(businessRecordId, payId, {
      search: debouncedSearch || undefined,
      from: utcDayBoundary(dateRange.start),
      to: utcDayBoundary(dateRange.end || dateRange.start, true),
      workEntityId: employeeId || undefined,
      pageSize: PAGE_SIZE,
      cursor,
    }, abortController.signal)
      .then((result) => {
        if (requestId.current === currentRequest) setPage(result);
      })
      .catch((reason: unknown) => {
        if (abortController.signal.aborted || requestId.current !== currentRequest) return;
        if (reason instanceof PayRunAuditApiError && reason.status === 503) {
          setUnavailable(true);
          setPage(undefined);
          return;
        }
        setError(reason instanceof Error ? reason.message : 'Audit trail could not be loaded.');
        setPage(undefined);
      })
      .finally(() => {
        if (requestId.current === currentRequest) setLoading(false);
      });

    return () => abortController.abort();
  }, [businessRecordId, payId, debouncedSearch, employeeId, dateRange.start, dateRange.end, cursor, reloadKey]);

  const employeeOptions = useMemo<SelectFieldOption[]>(() => [
    { label: 'All employees', value: '' },
    ...(page?.employees ?? []).map((employee) => ({ label: employee.displayName, value: employee.workEntityId })),
  ], [page?.employees]);

  const columns = useMemo<TableColumn[]>(() => [
    {
      field: 'occurredAtUtc',
      header: 'Time',
      headerClassName: 'mgo-text-xs mgo-weight-strong mgo-color-muted',
      width: '10%',
      body: (row: PayRunAuditEvent) => {
        const timestamp = formatTime(row.occurredAtUtc);
        return (
          <div className="mgo-stack mgo-gap-0 mgo-text-xs">
            <span className="mgo-weight-strong">{timestamp.time}</span>
            <span className="mgo-color-muted">{timestamp.date}</span>
          </div>
        );
      },
    },
    {
      field: 'eventType',
      header: 'Event',
      headerClassName: 'mgo-text-xs mgo-weight-strong mgo-color-muted',
      width: '19%',
      body: (row: PayRunAuditEvent) => (
        <div className="mgo-stack mgo-gap-0 mgo-text-xs">
          <span className="mgo-weight-strong">{row.summary}</span>
          <span className="mgo-color-muted">{titleCase(row.eventType)}</span>
        </div>
      ),
    },
    {
      field: 'detail',
      header: 'Detail',
      headerClassName: 'mgo-text-xs mgo-weight-strong mgo-color-muted',
      width: '25%',
      body: (row: PayRunAuditEvent) => <span className="mgo-text-xs mgo-color-muted">{row.detail || '—'}</span>,
    },
    {
      field: 'comment',
      header: 'Comments',
      headerClassName: 'mgo-text-xs mgo-weight-strong mgo-color-muted',
      width: '20%',
      body: (row: PayRunAuditEvent) => <span className="mgo-text-xs mgo-color-muted">{row.comment || '—'}</span>,
    },
    {
      field: 'actorDisplayName',
      header: 'User',
      headerClassName: 'mgo-text-xs mgo-weight-strong mgo-color-muted',
      width: '16%',
      body: (row: PayRunAuditEvent) => (
        <div className="mgo-stack mgo-gap-0 mgo-text-xs">
          <span className="mgo-weight-strong">{row.actorDisplayName}</span>
          {row.actorRoleLabels && <span className="mgo-color-muted">{row.actorRoleLabels}</span>}
        </div>
      ),
    },
    {
      field: 'outcome',
      header: 'Status',
      headerClassName: 'mgo-text-xs mgo-weight-strong mgo-color-muted',
      width: '10%',
      body: (row: PayRunAuditEvent) => <Tag label={titleCase(row.outcome)} tone={outcomeTone(row.outcome)} />,
    },
  ], []);

  const hasFilters = Boolean(debouncedSearch || employeeId || dateRange.start || dateRange.end);
  const clearFilters = () => {
    setSearch('');
    setDebouncedSearch('');
    setEmployeeId('');
    setDateRange({ start: '', end: '' });
  };
  const retry = () => setReloadKey((key) => key + 1);
  const openDateRange = () => {
    setDraftDateRange(dateRange);
    setDateSelectionStep(dateRange.start && !dateRange.end ? 'end' : 'start');
    setHoveredDate(null);
  };
  const selectDate = (value: string) => {
    if (dateSelectionStep === 'start' || (draftDateRange.start && draftDateRange.end)) {
      setDraftDateRange({ start: value, end: '' });
      setDateSelectionStep('end');
      return;
    }

    setDraftDateRange(value < draftDateRange.start
      ? { start: value, end: draftDateRange.start }
      : { start: draftDateRange.start, end: value });
    setDateSelectionStep('start');
    setHoveredDate(null);
  };
  const cancelDateRange = () => dateRangePopoverRef.current?.hide();
  const applyDateRange = () => {
    setDateRange(draftDateRange);
    dateRangePopoverRef.current?.hide();
  };
  const goNext = () => {
    if (!page?.nextCursor) return;
    setCursorHistory((history) => [...history, cursor]);
    setCursor(page.nextCursor);
  };
  const goPrevious = () => {
    setCursorHistory((history) => {
      const previous = history.at(-1);
      setCursor(previous);
      return history.slice(0, -1);
    });
  };

  return (
    <section className="mgo-stack mgo-gap-m" aria-label="Pay Run audit trail">
      <div className="pay-run-audit-toolbar mgo-split mgo-gap-s mgo-align-end">
        <div className="pay-run-audit-toolbar__leading mgo-cluster mgo-gap-s mgo-align-end">
          <div className="pay-run-audit-toolbar__search">
            <TextField
              label="Search audit trail"
              hideLabel
              placeholder="Search employee, field or value..."
              leadingIcon="search"
              clearable
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onClear={() => setSearch('')}
            />
          </div>
          <Popover
            ref={dateRangePopoverRef}
            trigger={<Button label="Date range" variant="secondary" className="pay-run-audit-toolbar__date-trigger" />}
            header="Date range"
            footer={(
              <div className="mgo-cluster mgo-gap-s mgo-justify-end">
                <Button label="Cancel" variant="tertiary" size="small" onClick={cancelDateRange} />
                <Button label="Apply" size="small" onClick={applyDateRange} />
              </div>
            )}
            className="pay-run-audit-toolbar__date-popover"
            onShow={openDateRange}
          >
            <div className="mgo-stack mgo-gap-s">
              <div className="pay-run-audit-date-summary mgo-split mgo-gap-s mgo-padding-s mgo-background-neutral mgo-radius-strong">
                <span className="mgo-text-xs mgo-color-muted">{draftDateRange.start || 'Start date'}</span>
                <span className="mgo-text-xs mgo-color-muted">to</span>
                <span className="mgo-text-xs mgo-color-muted">{draftDateRange.end || 'End date'}</span>
              </div>
              <DatePicker
                dateFormat="ymd"
                value={draftDateRange.end || draftDateRange.start}
                rangeStart={draftDateRange.start}
                rangeEnd={draftDateRange.end}
                hoveredDate={hoveredDate}
                onHoveredDateChange={setHoveredDate}
                onSelect={selectDate}
                testId="audit-date-range-calendar"
              />
            </div>
          </Popover>
        </div>
        <div className="pay-run-audit-toolbar__trailing mgo-cluster mgo-gap-l mgo-align-center">
          <div className="pay-run-audit-toolbar__employee">
            <SelectField
              label="Employee"
              hideLabel
              searchable
              value={employeeId}
              options={employeeOptions}
              onChange={(value) => setEmployeeId(typeof value === 'string' ? value : '')}
            />
          </div>
          {page && <span className="mgo-text-xs mgo-color-muted">{page.totalCount.toLocaleString()} events</span>}
        </div>
      </div>

      {loading && !page && (
        <div className="pay-run-audit-state mgo-stack mgo-gap-s mgo-justify-center mgo-align-center" role="status" aria-live="polite">
          <Spinner />
          <span>Loading audit events…</span>
        </div>
      )}

      {!loading && unavailable && (
        <Callout tone="info" title="Audit trail unavailable">
          Lifecycle events will be available after the audit service is enabled for this environment.
        </Callout>
      )}

      {!loading && error && (
        <Callout tone="critical" title="Audit trail could not be loaded">
          <div className="mgo-stack mgo-gap-s">
            <span>{error}</span>
            <div><Button label="Try again" variant="secondary" size="small" onClick={retry} /></div>
          </div>
        </Callout>
      )}

      {!loading && !error && !unavailable && page?.items.length === 0 && (
        <div className="pay-run-audit-state mgo-stack mgo-justify-center mgo-align-center mgo-padding-m">
          <EmptyState
            variant="no-data"
            title={hasFilters ? 'No audit events match your filters' : 'No audit events available'}
            description={hasFilters ? 'Try adjusting the search, employee or date range.' : 'Lifecycle events will appear here as this Pay Run progresses.'}
            actions={hasFilters ? [<Button key="clear" label="Clear filters" variant="secondary" size="small" onClick={clearFilters} />] : undefined}
          />
        </div>
      )}

      {page && page.items.length > 0 && (
        <>
          <div className="pay-run-audit-table">
            <Table<PayRunAuditEvent>
              value={page.items}
              columns={columns}
              loading={loading}
              testId="pay-run-audit-table"
            />
          </div>
          <div className="mgo-split mgo-gap-s mgo-align-center">
            <span className="mgo-text-xs mgo-color-muted">{page.coverage.description}</span>
            <div className="mgo-cluster mgo-gap-s">
              <Button label="Previous" variant="secondary" size="small" disabled={cursorHistory.length === 0 || loading} onClick={goPrevious} />
              <Button label="Next" variant="secondary" size="small" disabled={!page.nextCursor || loading} onClick={goNext} />
            </div>
          </div>
        </>
      )}
    </section>
  );
};
